import { Link, useParams } from "react-router-dom";
import Helmet from "react-helmet";

import { useTranslation } from "@mywheels/common/lib/translation";
import { useGraphCMS } from "@mywheels/common/lib/useGraphCMS";

import { ScrollToTop } from "web/ui";
import { useRoutes } from "web/lib/i18n";

import { NotFoundPage } from "web/pages/NotFoundPage";

import { BlogCard } from "./BlogCard";
import { BlogToolbar } from "./BlogToolbar";
import { ToolbarSpacer } from "web/layout/Toolbar";
import { BlogHero } from "./BlogHero";
import {
  SearchBlogPostsByCategorySlugQuery,
  SearchBlogPostsQuery,
} from "./data";

export default function BlogArchivePage() {
  const { t } = useTranslation("page:BlogArchive");
  const routes = useRoutes();

  const params = useParams<{
    filter?: string;
    page?: string;
  }>();

  const categorySlug = params.filter || "alles";

  const pageNo = parseInt(params.page || "") || 1; // 1-based

  const { data: searchResults, isFetching } = useGraphCMS(
    categorySlug === "alles"
      ? SearchBlogPostsQuery
      : SearchBlogPostsByCategorySlugQuery,
    {
      categorySlug,
      offset: (pageNo - 1) * 9,
      limit: 9,
    }
  );

  if (searchResults && searchResults.aggregate.count === 0) {
    // TODO make this work layout-stylistically, somehow
    return (
      <>
        <ToolbarSpacer className="bg-gray" />
        <NotFoundPage />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{t("Blog")}</title>
        <link
          href={routes.BlogArchive.url({ filter: categorySlug })}
          rel="canonical"
        />
      </Helmet>

      <ScrollToTop reactToSearchChange>
        <BlogHero key={[categorySlug, pageNo].join("-")} small>
          <div className="absolute inset-0 bg-gray900 bg-opacity-20" />
          <div className="relative container text-white">
            <div className="row">
              <div className="col-12 md:col-7">
                <h1 className="text-white md:text-5xl mb-5">
                  <Link
                    to={routes.Blog.url()}
                    style={{ textDecoration: "none", color: "white" }}
                  >
                    {t("MyWheels Blog")}
                  </Link>
                </h1>
              </div>
            </div>
          </div>
        </BlogHero>

        <div className="container mb-12">
          <BlogToolbar loading={isFetching} />
          <div className="row mb-o">
            {searchResults?.edges.map((edge) => (
              <div key={edge.node.id} className="col-12 md:col-4">
                <BlogCard post={edge.node} />
              </div>
            ))}
          </div>
          <p className="mt-4 mb-6 text-center">
            {pageNo > 1 && (
              <Link
                className="btn btn-outline-blue mx-1"
                to={routes.BlogArchive.url({
                  filter: categorySlug,
                  page: pageNo === 2 ? undefined : pageNo - 1,
                })}
              >
                {t("Vorige pagina")}
              </Link>
            )}
            {searchResults?.pageInfo.hasNextPage && (
              <Link
                className="btn btn-blue mx-1"
                to={routes.BlogArchive.url({
                  filter: categorySlug,
                  page: pageNo + 1,
                })}
              >
                {t("Volgende pagina")}
              </Link>
            )}
          </p>
        </div>
      </ScrollToTop>
    </>
  );
}
